import isEqual from 'lodash/isEqual';
import { useRoute, useRouter, type RouteLocationNormalized } from 'vue-router';
import type { UnwrapRef } from 'vue';
import { base64ToObject, objectToBase64 } from '@shared/libs/base64';
import { stripFilterOperandIDs, validateSearchOptions } from '.';
import {
  type DatasetRequest,
  type OffsetPaginationRequest,
  type GroupingFilterExpression,
  FilterOperator,
  isGroupingFilterExpression,
} from './types';

export type SearchQueryParams = Omit<DatasetRequest, 'filter'> & {
  // We use the more specific GroupingFilterExpression so simplify the types on consumption
  filter?: GroupingFilterExpression;
  page?: OffsetPaginationRequest;
};

// Builds the `q` query value from search params.
// If there is no filter, encodes an empty filter so the URL still overrides
// any default filter from the composable.
export function encodeSearchQueryParams(params: SearchQueryParams) {
  const payload: SearchQueryParams = {
    ...params,
    filter: params.filter ?? {
      operator: FilterOperator.And,
      operands: [],
    },
  };
  return { q: objectToBase64(payload as Record<string, any>) };
}

// Reads search params back out of `q`, mirroring encodeSearchQueryParams.
// Anything that reads the on-screen search without owning it (an export button in
// a page hero, say) needs this too, and a second copy would drift from the encode.
export function decodeSearchQueryParams(
  query: RouteLocationNormalized['query'],
): SearchQueryParams | undefined {
  const { q } = query;
  if (!q || typeof q !== 'string') return undefined;

  const decoded = base64ToObject(q);
  if (!validateSearchOptions(decoded)) return undefined;

  return decoded as SearchQueryParams;
}

// Sets or removes search `q` while preserving other query keys (e.g. controlID on assessment).
function mergeSearchQueryIntoRouteQuery(
  query: RouteLocationNormalized['query'],
  qNext: string | undefined,
): RouteLocationNormalized['query'] {
  const next = { ...query };
  if (qNext === undefined) {
    Reflect.deleteProperty(next, 'q');
  } else {
    next.q = qNext;
  }
  return next;
}

type SearchQueryParamsWithExtras<TRequestExtras extends object> = SearchQueryParams &
  UnwrapRef<TRequestExtras>;

type NoExtras = Record<string, never>;

export default function useSearchQueryParams<TRequestExtras extends object = NoExtras>(
  defaultVal?: MaybeRefOrGetter<DatasetRequest & TRequestExtras>,
) {
  const router = useRouter();
  const route = useRoute();

  function getValuesFromQueryParam() {
    const { q } = route.query;
    if (!q || typeof q !== 'string') return undefined;

    const decoded = decodeSearchQueryParams(route.query);
    if (!decoded) {
      // invalid object so don't set and remove from query param
      void router.replace({ query: mergeSearchQueryIntoRouteQuery(route.query, undefined) });
    }
    // The merge below has always leaned on the decoded value being loose: a `q`
    // from an older link can carry a pagination shape this composable does not
    // expose. Narrowing it properly means widening `defaultVal` too, which
    // ripples through useTypedQueryParams, so it stays as it was.
    return decoded as any;
  }

  const mergedDefaultVal = computed<SearchQueryParamsWithExtras<TRequestExtras>>(() => {
    const merged = {
      filter: undefined,
      sort: undefined,
      page: { limit: 50, offset: 0 },
      ...toValue(defaultVal),
      ...getValuesFromQueryParam(),
    };
    // A decoded `q` (e.g. an old deep link) can also carry operand ids
    // without going through Filters.vue's own stripping - strip here too.
    return {
      ...merged,
      filter: stripFilterOperandIDs(merged.filter) as GroupingFilterExpression | undefined,
    };
  });

  // oxlint-disable-next-line local-rules/no-ref-object-reactivity-loss
  const params = reactive(mergedDefaultVal.value);

  // Reset offset when filters change
  watch(
    () => params.filter,
    (newFilters, oldFilters) => {
      const filtersChanged = !isEqual(
        stripFilterOperandIDs(newFilters),
        stripFilterOperandIDs(oldFilters),
      );

      if (filtersChanged && params.page) {
        params.page.offset = 0;
      }
    },
  );

  // Reset offset when the sort changes. Compared by value, not identity: a URL
  // change reassigns a freshly decoded `sort`, which must not read as re-sorting.
  watch(
    () => params.sort,
    (sort, previousSort) => {
      if (isEqual(sort, previousSort)) return;
      if (params.page) params.page.offset = 0;
    },
  );

  const reset = async () => {
    await router.replace({ query: mergeSearchQueryIntoRouteQuery(route.query, undefined) });
    Object.assign(params, mergedDefaultVal.value);
  };

  const hasChanged = computed(
    () =>
      !isEqual(
        { ...params, filter: stripFilterOperandIDs(params.filter), page: undefined },
        {
          ...toValue(defaultVal),
          filter: stripFilterOperandIDs(toValue(defaultVal)?.filter),
          page: undefined,
        },
      ),
  );

  // Generally try to avoid mutating `syncEnabled` outside this component:
  // it's for emergencies only
  const syncEnabled = ref(true);
  let hasPendingUpdate = false;
  async function disableSyncWhile(cb: () => Promise<unknown>) {
    syncEnabled.value = false;

    await cb();

    setTimeout(() => {
      syncEnabled.value = true;
    });
  }

  function updateParams() {
    if (!syncEnabled.value) {
      hasPendingUpdate = true;
      return;
    }

    void disableSyncWhile(async () => {
      const defaultValResolved: DatasetRequest = toValue(defaultVal) ?? {};
      const defaultGroupingOperands =
        defaultValResolved.filter && isGroupingFilterExpression(defaultValResolved.filter)
          ? defaultValResolved.filter.operands.length
          : 0;
      // When callers pass default filter/sort, keep `q` in sync with params (bookmarkable URL).
      // When there are no explicit defaults, omit `q` once the user is back at baseline.
      const omitQWhenUnchangedAtBaseline =
        defaultGroupingOperands === 0 && !defaultValResolved.sort?.length;

      if (!hasChanged.value && params.page?.offset === 0 && omitQWhenUnchangedAtBaseline) {
        if (route.query.q) {
          await router.replace({ query: mergeSearchQueryIntoRouteQuery(route.query, undefined) });
        }
        return;
      }

      const encoded = encodeSearchQueryParams(params);
      if (encoded.q !== route.query.q) {
        await router.replace({ query: mergeSearchQueryIntoRouteQuery(route.query, encoded.q) });
      }
    });
  }

  watch(params, updateParams, { deep: true });

  // A change to `params` while syncing is off would otherwise never reach the URL
  // unless another followed it, such as a filter reset landing just after the
  // write a page makes as it loads. It is written once syncing is back on.
  watch(syncEnabled, (enabled) => {
    if (!enabled || !hasPendingUpdate) return;
    hasPendingUpdate = false;
    updateParams();
  });

  // Realigns `params` with the current defaults (the baseline `hasChanged` is
  // measured against).
  function resync() {
    const newVal = mergedDefaultVal.value;
    if (!newVal) return;
    // for each property in val, replace it in params
    for (const key of Object.keys(newVal)) {
      (params as any)[key] = (newVal as any)[key];
    }
  }

  watch(
    mergedDefaultVal,
    () => {
      if (!syncEnabled.value) return;
      resync();
    },
    { deep: true },
  );

  // Runs callback with param syncing frozen, then resyncs.
  async function resyncParamsAfter(callback: () => Promise<unknown>) {
    await disableSyncWhile(callback);
    resync();
  }

  const hasActiveFilters = computed(() => !!params.filter?.operands?.length);

  return {
    params,
    hasChanged,
    updateParams,
    reset,
    resyncParamsAfter,
    hasActiveFilters,
  };
}
